/**
 * Bank — wallet and transaction history for teachers and students.
 *
 * Teacher view : income from live sessions + capsules (credits).
 * Student view : expenses per live session (debits) + add-balance via AT Protocol.
 *
 * Fully theme-aware (system / light / dark) via useStudioChrome().
 * Animated sparkline: Skia (native) / SVG (web).
 */
import {useCallback, useEffect, useMemo, useState} from 'react'
import {
  ActivityIndicator,
  Platform,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'
import Animated, {
  Easing,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import Svg, {
  Circle,
  Defs,
  LinearGradient as SvgGradient,
  Path,
  Stop,
} from 'react-native-svg'
import {LinearGradient} from 'expo-linear-gradient'
import * as WebBrowser from 'expo-web-browser'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {BSKY_SERVICE} from '#/lib/constants'
import {DASHBOARD_ACCENT} from '#/lib/dashboardPalette'
import {useProfileQuery} from '#/state/queries/profile'
import {useInvalidateWallet, useWalletQuery} from '#/state/queries/wallet'
import {useAgent, useSession} from '#/state/session'
import {DinarSymbol} from '#/screens/Dashboard/components/DinarSymbol'
import {
  BillsProvider,
  useBills,
} from '#/screens/Dashboard/contexts/BillsContext'
import BuyBillsSheet from '#/components/BuyBillsSheet'
import * as Dialog from '#/components/Dialog'
import * as Layout from '#/components/Layout'
import {readableInkOn, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome} from '#/themes-studio/useChrome'
import {
  extractCreds,
  getBrokerStatus,
  requestWithdrawal,
  type SavedAccount,
} from './lib/atipayment'

// ── Only import Skia on native ─────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let SkiaCanvas: any = null
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let SkiaPath: any = null
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let SkiaGroup: any = null
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let SkiaLinearGradient: any = null
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let Skia: any = null
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let skiaVec: any = null
if (Platform.OS !== 'web') {
  try {
    const skia = require('@shopify/react-native-skia')
    SkiaCanvas = skia.Canvas
    SkiaPath = skia.Path
    SkiaGroup = skia.Group
    SkiaLinearGradient = skia.LinearGradient
    Skia = skia.Skia
    skiaVec = skia.vec
  } catch {
    /* Skia not available */
  }
}

// ── Types ──────────────────────────────────────────────────────────────────────
interface Transaction {
  id: string
  type:
    | 'debit'
    | 'credit'
    | 'platform_fee'
    | 'bill_purchase'
    | 'topup'
    | 'consume'
    | 'withdrawal'
  amount: number
  description: string
  sessionId: string | null
  counterpartyId: string | null
  timestamp: number
  signature: string
  balanceAfter: number
}

interface Wallet {
  userId: string
  balance: number
  currency: string // ISO 4217, from server WALLET_CURRENCY config
  conversionRatio: number // local-currency units per 1 TND
  transactions: Transaction[]
}

// ── Constants — match server defaults ─────────────────────────────────────────
// Keep in sync with server env: BILLING_RATE
const BILLING_RATE = 0.08 // TND per live-session minute

// ── Helpers ────────────────────────────────────────────────────────────────────
function relativeDay(ts: number): string {
  const diff = Date.now() - ts
  const days = Math.floor(diff / 86400000)
  if (days === 0) return "Aujourd'hui"
  if (days === 1) return 'Hier'
  return `Il y a ${days} j`
}

function txCategory(tx: Transaction): {icon: string; label: string} {
  const desc = (tx.description ?? '').toLowerCase()
  if (tx.type === 'topup') return {icon: 'plus-circle', label: 'Recharge'}
  if (tx.type === 'withdrawal') return {icon: 'download', label: 'Retrait'}
  if (tx.type === 'bill_purchase')
    return {icon: 'shopping-bag', label: 'Achat bills'}
  if (tx.type === 'consume') return {icon: 'book-open', label: 'Capsule play'}
  if (tx.type === 'platform_fee')
    return {icon: 'percent', label: 'Frais plateforme'}
  if (desc.includes('live') || desc.includes('session'))
    return {icon: 'video', label: 'Session live'}
  if (desc.includes('challenge') || desc.includes('défi'))
    return {icon: 'zap', label: 'Challenge'}
  if (
    desc.includes('capsule') ||
    desc.includes('revenu') ||
    desc.includes('atiproto')
  )
    return {icon: 'book-open', label: 'Capsule'}
  return {
    icon: tx.type === 'credit' ? 'arrow-down-left' : 'arrow-up-right',
    label: tx.type === 'credit' ? 'Crédit' : 'Débit',
  }
}

// ── Animated Sparkline — Skia native / SVG web ─────────────────────────────────
function AnimatedSparkline({
  amounts,
  color,
  width = 180,
  height = 52,
}: {
  amounts: number[]
  color: string
  width?: number
  height?: number
}) {
  const colorsHook = useStudioChrome()
  const amountsKey = amounts.join(',')
  const progress = useSharedValue(0)

  useEffect(() => {
    progress.value = 0
    progress.value = withTiming(1, {
      duration: 1000,
      easing: Easing.out(Easing.cubic),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountsKey])

  const max = Math.max(...amounts, 0.01)
  const padX = 4
  const padY = 6
  const xs = amounts.map(
    (_, i) => padX + (i / Math.max(amounts.length - 1, 1)) * (width - padX * 2),
  )
  const ys = amounts.map(v => padY + (1 - v / max) * (height - padY * 2))

  const pathD = xs
    .map((x, i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${ys[i].toFixed(1)}`)
    .join(' ')
  const areaD = `${pathD} L ${xs[xs.length - 1].toFixed(1)} ${(height - padY).toFixed(1)} L ${xs[0].toFixed(1)} ${(height - padY).toFixed(1)} Z`
  const gradId = `sparkGrad_${color.replace('#', '')}`

  // Always call hooks unconditionally — no hooks inside conditionals
  const skiaPaths = useMemo(() => {
    if (Platform.OS === 'web' || !SkiaCanvas || !Skia) return null
    try {
      const line = Skia.Path.Make()
      xs.forEach((x, i) => {
        if (i === 0) line.moveTo(x, ys[i])
        else line.lineTo(x, ys[i])
      })
      const area = Skia.Path.Make()
      xs.forEach((x, i) => {
        if (i === 0) area.moveTo(x, ys[i])
        else area.lineTo(x, ys[i])
      })
      area.lineTo(xs[xs.length - 1], height - padY + 2)
      area.lineTo(xs[0], height - padY + 2)
      area.close()
      return {line, area}
    } catch {
      return null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountsKey, width, height])

  const clipRect = useDerivedValue(() => ({
    x: 0,
    y: 0,
    width: progress.value * (width + 10),
    height: height + 10,
  }))

  const areaOpacity = useDerivedValue(() => progress.value * 0.28)

  if (amounts.every(a => a === 0)) {
    return (
      <View
        style={{width, height, alignItems: 'center', justifyContent: 'center'}}>
        <Text style={{fontSize: 10, color: colorsHook.textMuted}}>
          Aucune activité
        </Text>
      </View>
    )
  }

  // Native: Skia canvas with animated clip
  if (Platform.OS !== 'web' && SkiaCanvas && Skia && skiaPaths) {
    return (
      <SkiaCanvas style={{width, height}}>
        <SkiaGroup clip={clipRect}>
          <SkiaPath path={skiaPaths.area} color={color} opacity={areaOpacity}>
            <SkiaLinearGradient
              start={skiaVec(0, 0)}
              end={skiaVec(0, height)}
              colors={[color + '88', color + '00']}
            />
          </SkiaPath>
          <SkiaPath
            path={skiaPaths.line}
            color={color}
            style="stroke"
            strokeWidth={2.5}
            strokeCap="round"
            strokeJoin="round"
          />
        </SkiaGroup>
      </SkiaCanvas>
    )
  }

  // Web / Skia unavailable: SVG fallback
  return renderSVGSparkline()

  function renderSVGSparkline() {
    return (
      <View style={{width, height}}>
        <Svg width={width} height={height}>
          <Defs>
            <SvgGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <Stop offset="0" stopColor={color} stopOpacity={0.3} />
              <Stop offset="1" stopColor={color} stopOpacity={0} />
            </SvgGradient>
          </Defs>
          <Path d={areaD} fill={`url(#${gradId})`} />
          <Path
            d={pathD}
            stroke={color}
            strokeWidth={2}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {xs.map((x, i) =>
            amounts[i] > 0 ? (
              <Circle
                key={i}
                cx={x}
                cy={ys[i]}
                r={3.5}
                fill={color}
                stroke={colorsHook.frame}
                strokeWidth={1.5}
              />
            ) : null,
          )}
        </Svg>
      </View>
    )
  }
}

// ── Recharge Dialog — student wallet top-up in dinars ────────────────────────
const TOP_UP_PRESETS = [20, 50, 80, 100]

function RechargeDialogInner({
  control,
  onSuccess,
  colors,
}: {
  control: ReturnType<typeof Dialog.useDialogControl>
  onSuccess: (amount: number) => void
  colors: StudioChrome
}) {
  const {t: l} = useLingui()
  const agent = useAgent()
  const isOpen = control.isOpen
  const [selected, setSelected] = useState<number | null>(null)
  const [customAmt, setCustomAmt] = useState('')
  const [phase, setPhase] = useState<
    'select' | 'processing' | 'polling' | 'done' | 'error'
  >('select')
  const [_sessionId, setSessionId] = useState<string | null>(null)
  const [topupErr, setTopupErr] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setSelected(null)
      setCustomAmt('')
      setPhase('select')
      setTopupErr(null)
      setSessionId(null)
    }
  }, [isOpen])

  const amount = selected ?? (parseFloat(customAmt) || 0)
  const canConfirm = amount >= 1
  const canClose = phase !== 'processing' && phase !== 'polling'
  // The confirm button's ink, from what the button is actually filled with: the
  // accent while it can be pressed, a hairline when it cannot. A fixed white
  // read on both in light mode, where the disabled fill is nearly that white.
  const confirmInk = canConfirm ? colors.onAccent : colors.textFaint

  const handleConfirm = useCallback(async () => {
    if (!canConfirm) return
    const accessJwt = agent.session?.accessJwt
    if (!accessJwt) {
      setTopupErr('Non authentifié.')
      setPhase('error')
      return
    }
    setPhase('processing')
    setTopupErr(null)
    try {
      // Step 1: create a Flouci payment session server-side
      const initRes = await fetch(
        `${BSKY_SERVICE}/api/topup/initiate-redirect`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessJwt}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({amountLocal: amount, provider: 'flouci'}),
        },
      )
      if (!initRes.ok) {
        const err = await initRes.json().catch(() => ({}))
        throw new Error(
          (err as {error?: string}).error ?? "Échec de l'initialisation",
        )
      }
      const {sessionId: sid, paymentUrl} = (await initRes.json()) as {
        sessionId: string
        paymentUrl: string
      }
      setSessionId(sid)

      // Step 2: open the Flouci payment page in the system browser
      await WebBrowser.openBrowserAsync(paymentUrl, {
        presentationStyle: WebBrowser.WebBrowserPresentationStyle.PAGE_SHEET,
        dismissButtonStyle: 'cancel',
      })

      // Step 3: poll for completion (user returned from browser)
      setPhase('polling')
      let attempts = 0
      const maxAttempts = 12 // 12 × 5 s = 60 s timeout
      const poll = async (): Promise<void> => {
        if (attempts >= maxAttempts) {
          throw new Error(
            'Délai expiré — vérifiez votre solde dans quelques instants.',
          )
        }
        attempts++
        await new Promise(r => setTimeout(r, 5000))
        const statusRes = await fetch(
          `${BSKY_SERVICE}/api/topup/status?sessionId=${encodeURIComponent(sid)}`,
          {headers: {Authorization: `Bearer ${accessJwt}`}},
        )
        if (!statusRes.ok) return poll()
        const {status, solde} = (await statusRes.json()) as {
          status: string
          solde?: number
        }
        if (status === 'completed') {
          control.close(() => onSuccess(solde ?? amount))
          setPhase('done')
        } else if (status === 'failed' || status === 'cancelled') {
          throw new Error('Paiement annulé ou refusé.')
        } else {
          return poll()
        }
      }
      await poll()
    } catch (err) {
      setTopupErr(err instanceof Error ? err.message : 'Erreur de rechargement')
      setPhase('error')
    }
  }, [canConfirm, amount, onSuccess, control, agent])

  return (
    <>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={l`Recharge balance`} style={{gap: 14}}>
        <Dialog.Close />

        {/* Header */}
        <View style={ssStyles.header}>
          <Feather name="plus-circle" size={22} color={colors.accentFill} />
          <Text style={[ssStyles.title, {color: colors.text}]}>
            <Trans>Recharge balance</Trans>
          </Text>
        </View>
        <Text style={[ssStyles.sub, {color: colors.textMuted}]}>
          <Trans>
            Add dinars to your balance to pay for live sessions and purchase
            bills.
          </Trans>
        </Text>

        {(phase === 'select' || phase === 'error') && (
          <>
            {/* Preset amounts */}
            <View style={ssStyles.pkgGrid}>
              {TOP_UP_PRESETS.map(p => {
                const sel = selected === p
                return (
                  <TouchableOpacity
                    accessibilityRole="button"
                    key={p}
                    style={[
                      ssStyles.pkgCard,
                      {
                        borderColor: sel ? colors.accentFill : colors.border,
                        backgroundColor: sel
                          ? colors.accentFill + '14'
                          : colors.frame,
                      },
                    ]}
                    onPress={() => {
                      setSelected(p)
                      setCustomAmt('')
                    }}
                    activeOpacity={0.75}>
                    <DinarSymbol
                      size={18}
                      color={sel ? colors.accentFill : colors.textMuted}
                    />
                    <Text
                      style={[
                        ssStyles.pkgBills,
                        {color: sel ? colors.accentFill : colors.text},
                      ]}>
                      {p}
                    </Text>
                  </TouchableOpacity>
                )
              })}
            </View>

            {/* Custom amount input */}
            <View
              style={[
                ssStyles.customRow,
                {
                  borderColor:
                    selected === null && customAmt
                      ? colors.accentFill
                      : colors.border,
                  backgroundColor: colors.frame,
                },
              ]}>
              <DinarSymbol size={16} color={colors.textMuted} />
              <TextInput
                accessibilityLabel="Text input field"
                accessibilityHint="Enter a custom amount in dinars"
                style={[ssStyles.customInput, {color: colors.text}]}
                placeholder={l`Custom amount`}
                placeholderTextColor={colors.textMuted}
                keyboardType="decimal-pad"
                value={customAmt}
                onChangeText={v => {
                  setCustomAmt(v)
                  setSelected(null)
                }}
              />
            </View>

            {topupErr && (
              <View
                style={[
                  ssStyles.errorBox,
                  {
                    backgroundColor: colors.danger + '15',
                    borderColor: colors.danger + '40',
                  },
                ]}>
                <Feather name="alert-circle" size={14} color={colors.danger} />
                <Text style={[ssStyles.errorText, {color: colors.danger}]}>
                  {topupErr}
                </Text>
              </View>
            )}

            <TouchableOpacity
              accessibilityRole="button"
              style={[
                ssStyles.confirmBtn,
                {
                  backgroundColor: canConfirm
                    ? colors.accentFill
                    : colors.border,
                },
              ]}
              onPress={handleConfirm}
              disabled={!canConfirm}
              activeOpacity={0.8}>
              <Feather name="check-circle" size={16} color={confirmInk} />
              {canConfirm ? (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 3,
                  }}>
                  <Text style={[ssStyles.confirmText, {color: confirmInk}]}>
                    <Trans>Recharge</Trans>
                  </Text>
                  <DinarSymbol size={13} color={confirmInk} />
                  <Text style={[ssStyles.confirmText, {color: confirmInk}]}>
                    {amount.toFixed(2)}
                  </Text>
                </View>
              ) : (
                <Text style={[ssStyles.confirmText, {color: confirmInk}]}>
                  <Trans>Choose an amount</Trans>
                </Text>
              )}
            </TouchableOpacity>
          </>
        )}

        {(phase === 'processing' || phase === 'polling') && (
          <View style={ssStyles.centerBlock}>
            <ActivityIndicator size="large" color={colors.accentFill} />
            <Text style={[ssStyles.centerText, {color: colors.textMuted}]}>
              {phase === 'polling'
                ? 'Vérification du paiement Flouci…'
                : 'Ouverture de la page de paiement…'}
            </Text>
          </View>
        )}

        <TouchableOpacity
          accessibilityRole="button"
          style={[
            ssStyles.cancelBtn,
            {borderColor: colors.border, opacity: canClose ? 1 : 0.4},
          ]}
          onPress={() => {
            if (canClose) control.close()
          }}
          disabled={!canClose}>
          <Text style={[ssStyles.cancelText, {color: colors.textMuted}]}>
            <Trans>Close</Trans>
          </Text>
        </TouchableOpacity>
      </Dialog.ScrollableInner>
    </>
  )
}

function RechargeDialog({
  control,
  onSuccess,
  colors,
}: {
  control: ReturnType<typeof Dialog.useDialogControl>
  onSuccess: (amount: number) => void
  colors: StudioChrome
}) {
  return (
    <Dialog.Outer control={control}>
      <RechargeDialogInner
        control={control}
        onSuccess={onSuccess}
        colors={colors}
      />
    </Dialog.Outer>
  )
}

const ssStyles = StyleSheet.create({
  header: {flexDirection: 'row', alignItems: 'center', gap: 10},
  title: {fontSize: 20, fontWeight: '900'},
  sub: {fontSize: 13, lineHeight: 18},
  pkgGrid: {flexDirection: 'row', gap: 10},
  pkgCard: {
    flex: 1,
    borderRadius: 16,
    borderWidth: 1.5,
    paddingVertical: 14,
    alignItems: 'center',
    gap: 5,
  },
  pkgBills: {fontSize: 22, fontWeight: '900'},
  customRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 14,
    borderWidth: 1.5,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  customInput: {flex: 1, fontSize: 16, fontWeight: '700'},
  confirmBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 16,
    paddingVertical: 16,
  },
  // No colour of its own: the confirm button is the accent when it can be
  // pressed and a hairline when it cannot, so its ink is set at the call site
  // from what is actually behind it.
  confirmText: {fontSize: 15, fontWeight: '900'},
  cancelBtn: {
    alignItems: 'center',
    paddingVertical: 14,
    borderRadius: 14,
    borderWidth: 1,
  },
  cancelText: {fontSize: 14, fontWeight: '600'},
  centerBlock: {alignItems: 'center', paddingVertical: 28, gap: 14},
  centerText: {fontSize: 13},
  errorBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    borderRadius: 14,
    borderWidth: 1,
    padding: 12,
  },
  errorText: {flex: 1, fontSize: 12, lineHeight: 18},
})

// ── Component ──────────────────────────────────────────────────────────────────
// Maps TaderokWebAppV2 session state to the shape bank.tsx expects.
function useAuthBridge(): {
  user: {role: 'teacher' | 'student'; id: string}
  savedAccounts: SavedAccount[]
} {
  const {currentAccount, accounts} = useSession()
  const {data: profile} = useProfileQuery({did: currentAccount?.did})
  const p = profile as typeof profile & {accountType?: string}
  const role: 'teacher' | 'student' =
    p?.accountType === 'creator' || p?.accountType === 'organisation'
      ? 'teacher'
      : 'student'

  const savedAccounts: SavedAccount[] = (accounts ?? []).map(acc => ({
    id: acc.did,
    handle: acc.handle,
    isBluesky: true,
    did: acc.did,
    accessJwt: acc.accessJwt,
    refreshJwt: acc.refreshJwt,
    pds: acc.pdsUrl,
  }))

  return {
    user: {role, id: currentAccount?.did ?? ''},
    savedAccounts,
  }
}

export default function BankScreen() {
  const {t: l} = useLingui()
  const {user, savedAccounts} = useAuthBridge()
  const {showToast} = useBills()
  const colors = useStudioChrome()
  const insets = useSafeAreaInsets()
  const isTeacher = user?.role === 'teacher'
  const userId = user?.id ?? ''

  const walletQuery = useWalletQuery()
  const invalidateWallet = useInvalidateWallet()
  const refreshing = walletQuery.isFetching && !walletQuery.isLoading
  const [filter, setFilter] = useState<
    'all' | 'live' | 'capsule' | 'challenge'
  >('all')
  const [copiedSig, setCopiedSig] = useState<string | null>(null)
  const rechargeControl = Dialog.useDialogControl()
  const [buySheetOpen, setBuySheetOpen] = useState(false)

  const [brokerPhase, setBrokerPhase] = useState<
    | 'idle'
    | 'checking'
    | 'onboarding'
    | 'ready'
    | 'withdraw_input'
    | 'withdrawing'
    | 'done'
    | 'error'
  >('idle')
  const [brokerError, setBrokerError] = useState<string | null>(null)
  const [withdrawAmt, setWithdrawAmt] = useState('')

  const currentCreds = useMemo(() => {
    const account = savedAccounts.find(a => a.id === user?.id)
    return account ? extractCreds(account) : null
  }, [user, savedAccounts])

  // Animate entrance
  const fadeAnim = useSharedValue(0)
  const scaleAnim = useSharedValue(0.96)

  const wallet: Wallet | null = walletQuery.data
    ? {
        userId,
        balance: walletQuery.data.balance,
        currency: 'TND',
        conversionRatio: 1.0,
        transactions: walletQuery.data.transactions,
      }
    : null

  const loading = walletQuery.isLoading
  const walletError = walletQuery.error ? String(walletQuery.error) : null

  const loadWallet = useCallback(async () => {
    await invalidateWallet()
  }, [invalidateWallet])

  // Trigger entrance animation whenever data arrives
  useEffect(() => {
    if (!walletQuery.isSuccess) return
    fadeAnim.value = 0
    scaleAnim.value = 0.96
    fadeAnim.value = withTiming(1, {duration: 450})
    scaleAnim.value = withSpring(1, {damping: 14, stiffness: 70})
  }, [walletQuery.isSuccess, fadeAnim, scaleAnim])

  const allTxs = wallet?.transactions ?? []

  const txs = useMemo(
    () =>
      allTxs.filter(t => {
        const desc = (t.description ?? '').toLowerCase()
        if (filter === 'all') return true
        if (filter === 'live')
          return desc.includes('live') || desc.includes('session')
        if (filter === 'capsule')
          return (
            !desc.includes('live') &&
            !desc.includes('session') &&
            !desc.includes('challenge')
          )
        if (filter === 'challenge')
          return desc.includes('challenge') || desc.includes('défi')
        return true
      }),
    [allTxs, filter],
  )

  const totalIn = allTxs
    .filter(t => t.type === 'credit')
    .reduce((s, t) => s + t.amount, 0)
  const totalOut = allTxs
    .filter(t => t.type === 'debit')
    .reduce((s, t) => s + t.amount, 0)
  // Role-specific live sessions: teacher counts income credits, student counts live debits
  const liveTxs = allTxs.filter(t => {
    const d = (t.description ?? '').toLowerCase()
    const isLive = d.includes('live') || d.includes('session')
    return isTeacher
      ? isLive && t.type === 'credit'
      : isLive && t.type === 'debit'
  })

  const weeklyAmounts = useMemo(() => {
    const days = Array(7).fill(0) as number[]
    const now = Date.now()
    const DAY = 86_400_000
    const targetType = isTeacher ? 'credit' : 'debit'
    for (const tx of allTxs) {
      if (tx.type !== targetType) continue
      const daysAgo = Math.floor((now - tx.timestamp) / DAY)
      if (daysAgo >= 0 && daysAgo < 7) days[6 - daysAgo] += tx.amount
    }
    return days
  }, [allTxs, isTeacher])

  const weekTotal = weeklyAmounts.reduce((a, b) => a + b, 0)

  const handleCashout = useCallback(async () => {
    if (!isTeacher) return
    if (!currentCreds) {
      setBrokerError(
        'Connecte ton compte Taderok pour accéder aux retraits réels.',
      )
      setBrokerPhase('error')
      return
    }
    setBrokerPhase('checking')
    setBrokerError(null)
    try {
      const status = await getBrokerStatus(currentCreds)
      if (status.ready) {
        setBrokerPhase('withdraw_input')
      } else if (status.onboardingUrl) {
        setBrokerPhase('onboarding')
        await WebBrowser.openBrowserAsync(status.onboardingUrl, {
          presentationStyle: WebBrowser.WebBrowserPresentationStyle.PAGE_SHEET,
          controlsColor: colors.success,
          dismissButtonStyle: 'cancel',
        })
        setBrokerPhase('checking')
        const rechk = await getBrokerStatus(currentCreds)
        setBrokerPhase(rechk.ready ? 'withdraw_input' : 'onboarding')
      } else {
        setBrokerError(
          `Statut du compte : ${status.status}. Contacte le support.`,
        )
        setBrokerPhase('error')
      }
    } catch (err) {
      setBrokerError(err instanceof Error ? err.message : 'Erreur de connexion')
      setBrokerPhase('error')
    }
  }, [isTeacher, currentCreds, colors.success])

  const handleWithdraw = useCallback(async () => {
    if (!currentCreds) return
    const amount = parseFloat(withdrawAmt)
    const balance = wallet?.balance ?? 0
    if (!amount || amount <= 0) {
      setBrokerError('Entrez un montant valide.')
      setBrokerPhase('error')
      return
    }
    if (amount > balance) {
      setBrokerError(
        `Solde insuffisant — disponible : ${balance.toFixed(2)} DT, demandé : ${amount.toFixed(2)} DT`,
      )
      setBrokerPhase('error')
      return
    }
    setBrokerPhase('withdrawing')
    try {
      await requestWithdrawal(currentCreds, userId, amount)
      setBrokerPhase('done')
      // Refresh wallet so the debited balance is reflected immediately
      void loadWallet()
    } catch (err) {
      setBrokerError(err instanceof Error ? err.message : 'Erreur de retrait')
      setBrokerPhase('error')
    }
  }, [currentCreds, withdrawAmt, wallet, userId, loadWallet])

  const handleTopupSuccess = useCallback(
    (amount: number) => {
      const currency = wallet?.currency ?? 'TND'
      showToast(`+${amount.toFixed(2)} ${currency} rechargés !`, 'bill')
      void loadWallet()
    },
    [showToast, loadWallet, wallet],
  )

  const s = useMemo(() => makeStyles(colors, insets), [colors, insets])

  // Must be unconditional — no hooks after early returns
  const animStyle = useAnimatedStyle(() => ({
    opacity: fadeAnim.value,
    transform: [{scale: scaleAnim.value}],
  }))

  if (loading) {
    return (
      <View style={[s.root, {alignItems: 'center', justifyContent: 'center'}]}>
        <ActivityIndicator color={colors.accentFill} size="large" />
        <Text style={{color: colors.textMuted, fontSize: 13, marginTop: 14}}>
          <Trans>Loading wallet…</Trans>
        </Text>
      </View>
    )
  }

  if (walletError && !wallet) {
    return (
      <View
        style={[
          s.root,
          {alignItems: 'center', justifyContent: 'center', padding: 32},
        ]}>
        <Feather name="alert-circle" size={40} color={colors.danger} />
        <Text
          style={{
            color: colors.text,
            fontSize: 16,
            fontWeight: '700',
            marginTop: 16,
            textAlign: 'center',
          }}>
          <Trans>Unable to load wallet</Trans>
        </Text>
        <Text
          style={{
            color: colors.textMuted,
            fontSize: 13,
            marginTop: 8,
            textAlign: 'center',
          }}>
          {walletError}
        </Text>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={() => loadWallet()}
          style={{
            marginTop: 24,
            paddingHorizontal: 24,
            paddingVertical: 12,
            borderRadius: 14,
            backgroundColor: colors.accentFill,
          }}>
          <Text
            style={{color: colors.onAccent, fontWeight: '700', fontSize: 14}}>
            <Trans>Retry</Trans>
          </Text>
        </TouchableOpacity>
      </View>
    )
  }

  const balance = wallet?.balance ?? 0
  const lowBalance = !isTeacher && balance < 1.0
  const accentColor = isTeacher ? colors.success : colors.accentFill
  /*
   * `accentColor` is the hue of the card — its wash, its border, its dot. Its
   * ink is that same hue stepped until it reads on the card, because neither end
   * of it is legible as text as it comes: the blue measures 4.46:1 on a light
   * card and 3.53:1 on a dim one, and the green is worse. Fills keep the raw
   * hue; only text and labels take this.
   */
  const accentInk = readableInkOn(accentColor, colors)
  const DAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D']
  const FILTERS = [
    {key: 'all' as const, label: l`All`, icon: 'list'},
    {key: 'live' as const, label: l`Live`, icon: 'video'},
    {key: 'capsule' as const, label: l`Capsules`, icon: 'book-open'},
    {key: 'challenge' as const, label: l`Challenges`, icon: 'zap'},
  ]

  return (
    <View style={s.root}>
      {/* ── Minimal sticky top bar ── */}
      <View style={[s.topBar, {paddingTop: insets.top + 12}]}>
        <View>
          <Text style={s.headerEyebrow}>
            {isTeacher ? l`Studio revenue` : l`My wallet`}
          </Text>
          <Text style={s.headerTitle}>
            <Trans>Wallet</Trans>
          </Text>
        </View>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={() => loadWallet()}
          style={s.refreshBtn}>
          {refreshing ? (
            <ActivityIndicator size="small" color={accentColor} />
          ) : (
            <Feather name="refresh-cw" size={17} color={colors.textMuted} />
          )}
        </TouchableOpacity>
      </View>

      {/* ── Full-page scroll ── */}
      <ScrollView
        style={s.scroll}
        contentContainerStyle={s.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => loadWallet()}
            tintColor={accentColor}
          />
        }
        showsVerticalScrollIndicator={false}>
        {/* ── Balance hero card ── */}
        <Animated.View style={animStyle}>
          <LinearGradient
            colors={[accentColor + '28', accentColor + '10', colors.surface]}
            start={{x: 0, y: 0}}
            end={{x: 1, y: 1}}
            style={[s.balanceCard, {borderColor: accentColor + '35'}]}>
            {/* Top: label + badge */}
            <View style={s.balanceCardTop}>
              <View
                style={[s.balancePill, {backgroundColor: accentColor + '20'}]}>
                <View
                  style={[s.balancePillDot, {backgroundColor: accentColor}]}
                />
                <Text style={[s.balancePillText, {color: accentInk}]}>
                  {isTeacher ? 'Solde gagné' : 'Solde disponible'}
                </Text>
              </View>
              <View
                style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
                {isTeacher ? (
                  <TouchableOpacity
                    accessibilityRole="button"
                    style={[
                      s.addBalanceBtn,
                      {
                        backgroundColor: colors.success + '18',
                        borderColor: colors.success + '40',
                        opacity:
                          brokerPhase === 'checking' ||
                          brokerPhase === 'withdrawing'
                            ? 0.6
                            : 1,
                      },
                    ]}
                    onPress={handleCashout}
                    disabled={
                      brokerPhase === 'checking' ||
                      brokerPhase === 'withdrawing'
                    }
                    activeOpacity={0.7}>
                    {brokerPhase === 'checking' ||
                    brokerPhase === 'withdrawing' ? (
                      <ActivityIndicator
                        size="small"
                        color={colors.success}
                        style={{width: 14, height: 14}}
                      />
                    ) : (
                      <Feather
                        name="download"
                        size={14}
                        color={colors.success}
                      />
                    )}
                    <Text
                      style={[s.addBalanceBtnText, {color: colors.success}]}>
                      Retirer
                    </Text>
                  </TouchableOpacity>
                ) : (
                  <>
                    <TouchableOpacity
                      accessibilityRole="button"
                      style={[
                        s.addBalanceBtn,
                        {
                          backgroundColor: accentColor + '18',
                          borderColor: accentColor + '40',
                        },
                      ]}
                      onPress={() => rechargeControl.open()}
                      activeOpacity={0.7}>
                      <Feather name="plus" size={14} color={accentColor} />
                      <Text style={[s.addBalanceBtnText, {color: accentColor}]}>
                        Recharger
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      accessibilityRole="button"
                      style={[
                        s.addBalanceBtn,
                        {
                          backgroundColor: colors.successTint,
                          borderColor: colors.successBorder,
                        },
                      ]}
                      onPress={() => setBuySheetOpen(true)}
                      activeOpacity={0.7}>
                      <Feather
                        name="shopping-bag"
                        size={14}
                        color={colors.success}
                      />
                      <Text
                        style={[
                          s.addBalanceBtnText,
                          {
                            color: readableInkOn(colors.success, colors),
                          },
                        ]}>
                        Bills
                      </Text>
                    </TouchableOpacity>
                  </>
                )}
                <View
                  style={[
                    s.balanceIconBg,
                    {backgroundColor: accentColor + '18'},
                  ]}>
                  <Feather
                    name={isTeacher ? 'trending-up' : 'credit-card'}
                    size={20}
                    color={accentColor}
                  />
                </View>
              </View>
            </View>

            {/* Main balance */}
            <View
              style={{flexDirection: 'row', alignItems: 'flex-end', gap: 4}}>
              <View style={{paddingBottom: 10}}>
                <DinarSymbol size={28} color={colors.text} />
              </View>
              <Text style={s.balanceAmount}>{balance.toFixed(2)}</Text>
            </View>
            <Text style={[s.balanceSubtitle, {color: accentInk}]}>
              {isTeacher
                ? `${liveTxs.length} session${liveTxs.length !== 1 ? 's' : ''} live · ${allTxs.length} transactions`
                : `≈ ${Math.floor(balance / BILLING_RATE)} min de session live`}
            </Text>

            {/* Sparkline row */}
            <View style={s.sparklineSection}>
              <View>
                {weekTotal > 0 ? (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 2,
                    }}>
                    <Text style={[s.sparklineLabel, {color: accentInk}]}>
                      +
                    </Text>
                    <DinarSymbol size={10} color={accentColor} />
                    <Text style={[s.sparklineLabel, {color: accentInk}]}>
                      {weekTotal.toFixed(2)} cette semaine
                    </Text>
                  </View>
                ) : (
                  <Text style={[s.sparklineLabel, {color: accentInk}]}>
                    Aucune activité
                  </Text>
                )}
                <View style={s.sparklineDays}>
                  {DAYS.map((d, i) => (
                    <Text
                      key={i}
                      style={[
                        s.sparklineDay,
                        weeklyAmounts[i] > 0 && {
                          color: accentInk,
                          fontWeight: '700',
                        },
                      ]}>
                      {d}
                    </Text>
                  ))}
                </View>
              </View>
              <AnimatedSparkline
                amounts={weeklyAmounts}
                color={accentColor}
                width={150}
                height={52}
              />
            </View>
          </LinearGradient>
        </Animated.View>

        {/* ── Stats row ── */}
        <View style={s.statsRow}>
          <View
            style={[
              s.statCard,
              {
                borderColor:
                  (isTeacher ? colors.success : colors.accentFill) + '30',
              },
            ]}>
            <View
              style={[
                s.statIconBg,
                {
                  backgroundColor:
                    (isTeacher ? colors.success : colors.danger) + '18',
                },
              ]}>
              <Feather
                name={isTeacher ? 'arrow-down-left' : 'arrow-up-right'}
                size={13}
                color={isTeacher ? colors.success : colors.danger}
              />
            </View>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 2}}>
              <DinarSymbol
                size={13}
                color={isTeacher ? colors.success : colors.danger}
              />
              <Text
                style={[
                  s.statValue,
                  {
                    color: readableInkOn(
                      isTeacher ? colors.success : colors.danger,
                      colors,
                    ),
                  },
                ]}>
                {isTeacher ? totalIn.toFixed(2) : totalOut.toFixed(2)}
              </Text>
            </View>
            <Text style={s.statLabel}>{isTeacher ? l`Earned` : l`Spent`}</Text>
          </View>
          <View style={[s.statCard, {borderColor: colors.border}]}>
            <View style={[s.statIconBg, {backgroundColor: colors.fill}]}>
              <Feather name="layers" size={13} color={colors.textMuted} />
            </View>
            <Text style={[s.statValue, {color: colors.text}]}>
              {allTxs.length}
            </Text>
            <Text style={s.statLabel}>
              <Trans>Transactions</Trans>
            </Text>
          </View>
          <View
            style={[s.statCard, {borderColor: DASHBOARD_ACCENT.live + '30'}]}>
            <View
              style={[
                s.statIconBg,
                {backgroundColor: DASHBOARD_ACCENT.live + '18'},
              ]}>
              <Feather
                name="video"
                size={13}
                color={readableInkOn(DASHBOARD_ACCENT.live, colors)}
              />
            </View>
            <Text
              style={[
                s.statValue,
                {color: readableInkOn(DASHBOARD_ACCENT.live, colors)},
              ]}>
              {liveTxs.length}
            </Text>
            <Text style={s.statLabel}>
              <Trans>Live sessions</Trans>
            </Text>
          </View>
        </View>

        {/* Low-balance warning */}
        {lowBalance && (
          <View style={s.warningBanner}>
            <View style={s.warningIconBg}>
              <Feather
                name="alert-triangle"
                size={14}
                color={readableInkOn(colors.danger, colors)}
              />
            </View>
            <Text
              style={[
                s.warningText,
                {color: readableInkOn(colors.danger, colors)},
              ]}>
              <Trans>
                Low balance — {Math.floor(balance / BILLING_RATE)} min of live
                remaining
              </Trans>
            </Text>
            <TouchableOpacity
              accessibilityRole="button"
              style={s.warningCta}
              onPress={() => rechargeControl.open()}>
              <Text
                style={[
                  s.warningCtaText,
                  {color: readableInkOn(colors.danger, colors)},
                ]}>
                <Trans>Recharge</Trans>
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* ── Transaction list ── */}
        <View style={s.section}>
          {/* Section header */}
          <View style={s.sectionHeader}>
            <View style={s.sectionTitleRow}>
              <View style={[s.sectionAccent, {backgroundColor: accentColor}]} />
              <Text style={s.sectionTitle}>
                <Trans>History</Trans>
              </Text>
            </View>
            <View style={[s.sectionCountBadge, {backgroundColor: colors.fill}]}>
              <Text style={[s.sectionCount, {color: colors.textMuted}]}>
                {txs.length} op.
              </Text>
            </View>
          </View>

          {/* Filter pills */}
          <View style={s.filterRow}>
            {FILTERS.map(f => {
              const active = filter === f.key
              return (
                <TouchableOpacity
                  accessibilityRole="button"
                  key={f.key}
                  style={[
                    s.filterBtn,
                    active && {
                      backgroundColor: accentColor + '18',
                      borderColor: accentColor,
                    },
                  ]}
                  onPress={() => setFilter(f.key)}
                  activeOpacity={0.7}>
                  <Feather
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    name={f.icon as any}
                    size={11}
                    color={active ? accentColor : colors.textMuted}
                  />
                  <Text
                    style={[
                      s.filterBtnText,
                      active && {color: accentInk, fontWeight: '700'},
                    ]}>
                    {f.label}
                  </Text>
                </TouchableOpacity>
              )
            })}
          </View>

          {/* Transaction rows */}
          {txs.length === 0 ? (
            <View style={s.empty}>
              <LinearGradient
                colors={[accentColor + '20', accentColor + '08']}
                style={s.emptyIcon}>
                <Feather
                  name={isTeacher ? 'trending-up' : 'inbox'}
                  size={30}
                  color={accentColor}
                />
              </LinearGradient>
              <Text style={s.emptyTitle}>
                <Trans>No transactions</Trans>
              </Text>
              <Text style={s.emptyText}>
                {isTeacher
                  ? l`Host a live session to start earning.`
                  : filter === 'all'
                    ? l`Join a live session to get started.`
                    : l`No "${filter}" transactions yet.`}
              </Text>
            </View>
          ) : (
            txs.map((tx, idx) => {
              const isCredit = tx.type === 'credit'
              const amtColor = isCredit ? colors.success : colors.danger
              const {icon, label} = txCategory(tx)
              const sigShort = tx.signature.slice(0, 16)
              const isCopied = copiedSig === sigShort
              return (
                <View
                  key={tx.id}
                  style={[
                    s.txRow,
                    idx === txs.length - 1 && {marginBottom: 0},
                  ]}>
                  <LinearGradient
                    colors={[amtColor + '28', amtColor + '12']}
                    style={s.txIconWrap}>
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    <Feather name={icon as any} size={17} color={amtColor} />
                  </LinearGradient>
                  <View style={s.txBody}>
                    <Text style={s.txDesc} numberOfLines={1}>
                      {tx.description}
                    </Text>
                    <View style={s.txMetaRow}>
                      <View
                        style={[
                          s.txCatBadge,
                          {backgroundColor: amtColor + '15'},
                        ]}>
                        <Text style={[s.txCatText, {color: amtColor}]}>
                          {label}
                        </Text>
                      </View>
                      <Text style={s.txDate}>{relativeDay(tx.timestamp)}</Text>
                    </View>
                  </View>
                  <View style={s.txRight}>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 2,
                      }}>
                      <Text style={[s.txAmount, {color: amtColor}]}>
                        {isCredit ? '+' : '−'}
                      </Text>
                      <DinarSymbol size={12} color={amtColor} />
                      <Text style={[s.txAmount, {color: amtColor}]}>
                        {tx.amount.toFixed(2)}
                      </Text>
                    </View>
                    <TouchableOpacity
                      accessibilityRole="button"
                      onPress={() => {
                        setCopiedSig(sigShort)
                        setTimeout(() => setCopiedSig(null), 2000)
                      }}
                      style={s.txSigBtn}
                      activeOpacity={0.6}>
                      <Feather
                        name="shield"
                        size={9}
                        color={isCopied ? colors.success : colors.textMuted}
                      />
                      <Text
                        style={[
                          s.txSigText,
                          isCopied && {color: colors.success},
                        ]}>
                        {isCopied ? 'Copié!' : sigShort.slice(0, 8)}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )
            })
          )}
        </View>

        {/* ── Cashout flow (teacher only) — shown when a withdrawal is in progress ── */}
        {isTeacher && brokerPhase !== 'idle' && (
          <View style={s.cashoutSection}>
            <LinearGradient
              colors={[colors.success + '18', colors.success + '08']}
              style={[s.cashoutCard, {borderColor: colors.success + '35'}]}>
              {brokerPhase === 'onboarding' && (
                <View style={s.cashoutInner}>
                  <ActivityIndicator size="small" color={colors.success} />
                  <View style={{flex: 1}}>
                    <Text style={[s.cashoutTitle, {color: colors.text}]}>
                      Configuration en cours…
                    </Text>
                    <Text style={s.cashoutSub}>
                      Finalisez la configuration du compte de paiement dans le
                      navigateur, puis revenez.
                    </Text>
                  </View>
                </View>
              )}
              {brokerPhase === 'checking' && (
                <View style={s.cashoutInner}>
                  <ActivityIndicator size="small" color={colors.success} />
                  <View style={{flex: 1}}>
                    <Text style={[s.cashoutTitle, {color: colors.text}]}>
                      Vérification du compte…
                    </Text>
                    <Text style={s.cashoutSub}>
                      Vérification du compte de paiement…
                    </Text>
                  </View>
                </View>
              )}
              {brokerPhase === 'withdraw_input' && (
                <View style={{gap: 12}}>
                  <View style={s.cashoutInner}>
                    <View
                      style={[
                        s.cashoutIconBg,
                        {backgroundColor: colors.success + '22'},
                      ]}>
                      <Feather
                        name="check-circle"
                        size={20}
                        color={colors.success}
                      />
                    </View>
                    <View style={{flex: 1}}>
                      <Text style={[s.cashoutTitle, {color: colors.text}]}>
                        Compte vérifié ✓
                      </Text>
                      <Text style={s.cashoutSub}>
                        Saisissez le montant à retirer
                      </Text>
                    </View>
                  </View>
                  <View style={{flexDirection: 'row', gap: 10}}>
                    <TextInput
                      accessibilityLabel="Text input field"
                      accessibilityHint="Enter the amount to withdraw in dinars"
                      value={withdrawAmt}
                      onChangeText={setWithdrawAmt}
                      placeholder={`Max ${balance.toFixed(2)} DT`}
                      placeholderTextColor={colors.textMuted}
                      keyboardType="decimal-pad"
                      style={[
                        s.withdrawInput,
                        {
                          color: colors.text,
                          borderColor: colors.success + '40',
                          backgroundColor: colors.frame,
                        },
                      ]}
                    />
                    <TouchableOpacity
                      accessibilityRole="button"
                      style={[
                        s.cashoutBtn,
                        {
                          backgroundColor: colors.success,
                          opacity:
                            !withdrawAmt || parseFloat(withdrawAmt) <= 0
                              ? 0.5
                              : 1,
                        },
                      ]}
                      onPress={handleWithdraw}
                      disabled={!withdrawAmt || parseFloat(withdrawAmt) <= 0}>
                      <Text style={s.cashoutBtnText}>
                        <Trans>Confirm</Trans>
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}
              {brokerPhase === 'withdrawing' && (
                <View style={s.cashoutInner}>
                  <ActivityIndicator size="small" color={colors.success} />
                  <View style={{flex: 1}}>
                    <Text style={[s.cashoutTitle, {color: colors.text}]}>
                      <Trans>Processing…</Trans>
                    </Text>
                    <Text style={s.cashoutSub}>
                      <Trans>Your request has been sent.</Trans>
                    </Text>
                  </View>
                </View>
              )}
              {brokerPhase === 'done' && (
                <View style={s.cashoutInner}>
                  <View
                    style={[
                      s.cashoutIconBg,
                      {backgroundColor: colors.success + '22'},
                    ]}>
                    <Feather
                      name="check-circle"
                      size={20}
                      color={colors.success}
                    />
                  </View>
                  <View style={{flex: 1}}>
                    <Text style={[s.cashoutTitle, {color: colors.text}]}>
                      <Trans>Request registered ✓</Trans>
                    </Text>
                    <Text style={s.cashoutSub}>
                      <Trans>Transfer within 1-2 business days.</Trans>
                    </Text>
                  </View>
                  <TouchableOpacity
                    accessibilityRole="button"
                    onPress={() => {
                      setBrokerPhase('idle')
                      setWithdrawAmt('')
                    }}
                    style={[
                      s.cashoutBtn,
                      {
                        backgroundColor: colors.fill,
                        borderWidth: 1,
                        borderColor: colors.border,
                      },
                    ]}>
                    <Text style={[s.cashoutBtnText, {color: colors.text}]}>
                      OK
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
              {brokerPhase === 'error' && (
                <View style={s.cashoutInner}>
                  <View
                    style={[
                      s.cashoutIconBg,
                      {backgroundColor: colors.danger + '18'},
                    ]}>
                    <Feather
                      name="alert-circle"
                      size={20}
                      color={colors.danger}
                    />
                  </View>
                  <View style={{flex: 1}}>
                    <Text style={[s.cashoutTitle, {color: colors.danger}]}>
                      Erreur
                    </Text>
                    <Text style={s.cashoutSub}>{brokerError}</Text>
                  </View>
                  <TouchableOpacity
                    accessibilityRole="button"
                    onPress={() => setBrokerPhase('idle')}
                    style={[
                      s.cashoutBtn,
                      {
                        backgroundColor: colors.fill,
                        borderWidth: 1,
                        borderColor: colors.border,
                      },
                    ]}>
                    <Text style={[s.cashoutBtnText, {color: colors.textMuted}]}>
                      Fermer
                    </Text>
                  </TouchableOpacity>
                </View>
              )}
            </LinearGradient>
          </View>
        )}

        <View style={{height: insets.bottom + 32}} />
      </ScrollView>

      {/* ── Recharge Dialog (student wallet top-up in dinars) ── */}
      {!isTeacher && (
        <RechargeDialog
          control={rechargeControl}
          onSuccess={handleTopupSuccess}
          colors={colors}
        />
      )}

      {/* ── Buy Bills Sheet ── */}
      {!isTeacher && (
        <BuyBillsSheet
          visible={buySheetOpen}
          onClose={() => setBuySheetOpen(false)}
        />
      )}
    </View>
  )
}

// ── Styles ─────────────────────────────────────────────────────────────────────
function makeStyles(
  colors: StudioChrome,
  _insets: {top: number; bottom: number},
) {
  return StyleSheet.create({
    root: {flex: 1, backgroundColor: colors.frame},
    scroll: {flex: 1},
    scrollContent: {paddingHorizontal: 18, paddingTop: 18},

    // ── Top bar
    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      paddingHorizontal: 18,
      paddingBottom: 14,
    },
    headerEyebrow: {
      fontSize: 11,
      fontWeight: '600',
      color: colors.textMuted,
      textTransform: 'uppercase',
      letterSpacing: 1.2,
      marginBottom: 2,
    },
    headerTitle: {
      fontSize: 26,
      fontWeight: '900',
      color: colors.text,
      letterSpacing: -0.5,
    },
    refreshBtn: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: colors.fill,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: colors.border,
    },

    // ── Balance card
    balanceCard: {
      borderRadius: 20,
      padding: 20,
      borderWidth: 1,
      marginBottom: 16,
      gap: 14,
    },
    balanceCardTop: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    balancePill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
    },
    balancePillDot: {width: 6, height: 6, borderRadius: 3},
    balancePillText: {fontSize: 11, fontWeight: '700', letterSpacing: 0.3},
    balanceIconBg: {
      width: 42,
      height: 42,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
    },
    addBalanceBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 7,
      borderRadius: 12,
      borderWidth: 1,
    },
    addBalanceBtnText: {fontSize: 11, fontWeight: '800'},
    balanceAmount: {
      fontSize: 52,
      fontWeight: '900',
      color: colors.text,
      letterSpacing: -2,
      lineHeight: 58,
    },
    balanceCurrency: {fontSize: 28, fontWeight: '700', letterSpacing: 0},
    balanceSubtitle: {fontSize: 12, fontWeight: '500', marginTop: -8},
    sparklineSection: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingTop: 4,
      borderTopWidth: 1,
      borderTopColor: colors.border + '60',
    },
    sparklineLabel: {fontSize: 11, fontWeight: '700', marginBottom: 6},
    sparklineDays: {flexDirection: 'row', gap: 12},
    sparklineDay: {
      fontSize: 9,
      color: colors.textMuted,
      width: 10,
      textAlign: 'center',
    },

    // ── Stats row
    statsRow: {flexDirection: 'row', gap: 10, marginBottom: 16},
    statCard: {
      flex: 1,
      backgroundColor: colors.frame,
      borderRadius: 14,
      borderWidth: 1,
      padding: 12,
      alignItems: 'center',
      gap: 6,
    },
    statIconBg: {
      width: 32,
      height: 32,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
    },
    statValue: {fontSize: 16, fontWeight: '900'},
    statLabel: {
      fontSize: 9,
      color: colors.textMuted,
      textAlign: 'center',
      fontWeight: '600',
      letterSpacing: 0.3,
    },

    // ── Warning
    warningBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      backgroundColor: colors.dangerTint,
      borderRadius: 14,
      padding: 12,
      borderWidth: 1,
      borderColor: colors.dangerBorder,
      marginBottom: 16,
    },
    warningIconBg: {
      width: 30,
      height: 30,
      borderRadius: 10,
      backgroundColor: colors.danger + '20',
      alignItems: 'center',
      justifyContent: 'center',
    },
    warningText: {flex: 1, fontSize: 12, fontWeight: '500'},
    warningCta: {
      backgroundColor: colors.danger + '20',
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 10,
    },
    warningCtaText: {fontSize: 11, fontWeight: '700'},

    // ── Section
    section: {paddingTop: 6},
    sectionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 14,
    },
    sectionTitleRow: {flexDirection: 'row', alignItems: 'center', gap: 8},
    sectionAccent: {width: 3, height: 16, borderRadius: 2},
    sectionTitle: {fontSize: 16, fontWeight: '800', color: colors.text},
    sectionCountBadge: {
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 10,
    },
    sectionCount: {fontSize: 11, fontWeight: '600'},

    // ── Filters
    filterRow: {
      flexDirection: 'row',
      gap: 8,
      marginBottom: 16,
      flexWrap: 'wrap',
    },
    filterBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 13,
      paddingVertical: 7,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },
    filterBtnText: {
      fontSize: 12,
      color: colors.textMuted,
      fontWeight: '600',
    },

    // ── Transaction rows
    txRow: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderRadius: 16,
      marginBottom: 10,
      borderWidth: 1,
      borderColor: colors.border,
      padding: 14,
      gap: 12,
    },
    txIconWrap: {
      width: 46,
      height: 46,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
    },
    txBody: {flex: 1, gap: 5},
    txDesc: {
      fontSize: 13,
      fontWeight: '700',
      color: colors.text,
      lineHeight: 18,
    },
    txMetaRow: {flexDirection: 'row', alignItems: 'center', gap: 8},
    txCatBadge: {paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8},
    txCatText: {fontSize: 10, fontWeight: '700'},
    txDate: {fontSize: 10, color: colors.textMuted},
    txRight: {alignItems: 'flex-end', gap: 4},
    txAmount: {fontSize: 16, fontWeight: '900'},
    txSigBtn: {flexDirection: 'row', alignItems: 'center', gap: 3},
    txSigText: {
      fontSize: 10,
      color: colors.textMuted,
      fontFamily: 'monospace',
    },

    // ── Empty
    empty: {alignItems: 'center', paddingVertical: 60, gap: 14},
    emptyIcon: {
      width: 72,
      height: 72,
      borderRadius: 22,
      alignItems: 'center',
      justifyContent: 'center',
    },
    emptyTitle: {fontSize: 17, fontWeight: '800', color: colors.text},
    emptyText: {
      fontSize: 13,
      color: colors.textMuted,
      textAlign: 'center',
      maxWidth: 260,
      lineHeight: 20,
    },

    // ── Cashout
    cashoutSection: {paddingTop: 16, paddingBottom: 4},
    cashoutCard: {borderRadius: 18, borderWidth: 1, padding: 18, gap: 10},
    cashoutInner: {flexDirection: 'row', alignItems: 'center', gap: 14},
    cashoutIconBg: {
      width: 44,
      height: 44,
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
    },
    cashoutTitle: {fontSize: 14, fontWeight: '800', marginBottom: 2},
    cashoutSub: {fontSize: 12, color: colors.textMuted, lineHeight: 16},
    cashoutBtn: {
      borderRadius: 12,
      paddingHorizontal: 16,
      paddingVertical: 10,
      alignItems: 'center',
      justifyContent: 'center',
    },
    cashoutBtnText: {fontSize: 13, fontWeight: '800', color: colors.onAccent},
    withdrawInput: {
      flex: 1,
      borderRadius: 12,
      borderWidth: 1,
      paddingHorizontal: 14,
      paddingVertical: 10,
      fontSize: 16,
      fontWeight: '700',
    },
  })
}

export function WalletScreen() {
  return (
    <BillsProvider>
      <Layout.Screen>
        <Layout.Center style={{flex: 1}}>
          <BankScreen />
        </Layout.Center>
      </Layout.Screen>
    </BillsProvider>
  )
}
